import { DynamicObject } from "@/types/common";
import { ReactNode, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { twMerge } from "tailwind-merge";

type Props = { children: ReactNode; className?: string; style?: DynamicObject };

const Tooltip = ({ children, className = "", style = {} }: Props) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [mounted, setMounted] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!tooltipRef.current) return;

      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      const { offsetWidth, offsetHeight } = tooltipRef.current;

      // Position tooltip to the right of cursor by default
      let x = clientX + 15;
      let y = clientY - offsetHeight / 2;

      // If tooltip would overflow right edge, position to the left of cursor
      if (x + offsetWidth > innerWidth - 15) {
        x = clientX - offsetWidth - 15;
      }

      // Keep tooltip within vertical bounds
      if (y < 10) y = 10;
      if (y + offsetHeight > innerHeight - 10)
        y = innerHeight - offsetHeight - 10;

      setPosition({ x, y });
    };

    document.addEventListener("mousemove", handleMouseMove);
    return () => document.removeEventListener("mousemove", handleMouseMove);
  }, []);

  const tooltipContent = (
    <div
      ref={tooltipRef}
      className={twMerge(
        "bg-card pointer-events-none fixed z-50 rounded border p-3 text-sm shadow-lg",
        className,
      )}
      style={{ ...style, left: position.x, top: position.y }}
    >
      {children}
    </div>
  );

  if (!mounted) return null;

  return createPortal(tooltipContent, document.body);
};

export default Tooltip;
